import { Col, Row } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { IAmlz } from 'types/amlz';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import Paragraph from 'components/atoms/Paragraph';
import TableComponent from 'components/atoms/TableComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import fileDownload from 'services/fileDownload';
import { historiePDFdownloadApi } from 'services/spravaEmitenta/amlzData';
import historickeZadostiApi, {
    EStavZadostAmlz,
    IZadosti,
    IZadostiTable
} from 'services/spravaEmitenta/historickeZadosti';
import vyjadreniCdcp from 'services/spravaEmitenta/vyjadreniCdcp';
import zrusitZadost from 'services/spravaEmitenta/zrusitZadost';

import routePathEnum from 'enums/routePathEnum';

interface IPrehledEmitent {}

const PrehledEmitent: React.FC<IPrehledEmitent> = () => {
    const { navigateWorkaround } = useNavigateWorkaround();
    const { setLevel } = useProfily();
    const { aktivniFirma, amlz } = useContext(Context);
    const [zadosti, setZadosti] = useState<IZadostiTable>();
    const [errStatus, setErrStatus] = useState<number>();
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingTable, setLoadingTable] = useState<boolean>(false);
    const [offset, setOffset] = useState<number>(0);
    const [modalMessage, setModalMessage] = useState<string>('');
    const [refetchData, setRefetchData] = useState<boolean>(false);

    const columns = [
        {
            title: <TranslateText>EMP_PODANE_ZADOSTI_DATUM_PODANI</TranslateText>,
            dataIndex: 'datum',
            key: 'datum',
            width: 200,
            render: (text: string) => dayjs(text).format(' DD.MM.YYYY')
        },
        {
            title: <TranslateText>TYP_ZADOSTI_TITLE</TranslateText>,
            dataIndex: 'typ',
            key: 'typ',
            width: 200,
            render: (text: string) => <TranslateText>{`AMLZ_TYPZADOSTI_${text}`}</TranslateText>
        },
        {
            title: <TranslateText>EMP_PODANE_ZADOSTI_STAV</TranslateText>,
            dataIndex: 'stav',
            key: 'stav',
            width: 200,
            render: (text: string) => (
                <TranslateText>{`STAVZADOSTI_SIMPLE_${text.toUpperCase().split(' ').join('_')}`}</TranslateText>
            )
        },
        {
            dataIndex: 'stav',
            key: 'stav',
            width: 200,
            render: (_: IZadosti, row: IZadosti, index: number) => (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-evenly',
                        flexGrow: 1
                    }}
                >
                    <AnchorComponent
                        id={`historickeZadostiZobrazitBtn${index}`}
                        onClick={() => {
                            if (
                                row.stav === EStavZadostAmlz.ROZP ||
                                row.stav === EStavZadostAmlz.CDCPKDOPL ||
                                row.stav === EStavZadostAmlz.DOPL
                            ) {
                                setLoadingTable(true);
                                setLevel('amlz', {
                                    stav: row.stav,
                                    idzadosti: row.idzadosti,
                                    ...(amlz?.idPoslednejHotovejZadosti && {
                                        idPoslednejHotovejZadosti: amlz?.idPoslednejHotovejZadosti
                                    })
                                } as IAmlz);
                                navigateWorkaround(
                                    aktivniFirma ? `/firma/${aktivniFirma?.iducet}${routePathEnum.AMLZ_FORMULAR}` : ''
                                );
                            } else {
                                setLoadingTable(true);
                                historiePDFdownloadApi(row.idzadosti)
                                    .then(data => {
                                        fileDownload(data.redirect, `${row.idzadosti}_ZadostAMLZ.pdf`).finally(() =>
                                            setLoadingTable(false)
                                        );
                                    })
                                    .catch(() => setLoadingTable(false));
                            }
                        }}
                    >
                        <TranslateText>ZOBRAZIT</TranslateText>
                    </AnchorComponent>

                    {row.stav === EStavZadostAmlz.KDOPL && (
                        <AnchorComponent
                            id={`historickeZadostiVyjadreniBtn${index}`}
                            onClick={() => {
                                setLoading(true);
                                vyjadreniCdcp(row.idzadosti)
                                    .then(data => setModalMessage(data.vyjadreni))
                                    .finally(() => setLoading(false));
                            }}
                        >
                            <TranslateText>VYJADRENI</TranslateText>
                        </AnchorComponent>
                    )}
                    {(row.stav === EStavZadostAmlz.KDOPL ||
                        row.stav === EStavZadostAmlz.KONTR ||
                        row.stav === EStavZadostAmlz.ROZP) && (
                        <AnchorComponent
                            id={`historickeZadostiZrusitBtn${index}`}
                            onClick={() => {
                                setLoading(true);
                                zrusitZadost(row.idzadosti).then(() => setRefetchData(val => !val));
                            }}
                        >
                            <TranslateText>ZRUSIT</TranslateText>
                        </AnchorComponent>
                    )}
                </div>
            )
        }
    ];

    useEffect(() => {
        // nacitanie dat pri zmene strany v table
        setLoading(true);
        setErrStatus(0);
        historickeZadostiApi({
            offset: offset,
            order: 'datum:desc',
            limit: 20
        })
            .then(resp => {
                setLevel('amlz', {
                    ...amlz,
                    ...(resp.zadosti?.length && {
                        ...resp.zadosti[0]
                    }),
                    ...(resp.lastid && {
                        idPoslednejHotovejZadosti: resp.lastid
                    })
                } as IAmlz);
                setZadosti(resp);
            })
            .catch((err: any) => {
                setErrStatus(err.response?.status);
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offset, refetchData]);

    return (
        <>
            {errStatus ? (
                <Row
                    justify="center"
                    align="middle"
                    style={{
                        height: '100%'
                    }}
                >
                    <Col span={16} flex={'0 0 auto'}>
                        <LoadingWrapper loading={loading}>
                            {errStatus === 404 ? (
                                <Title text="CHYBA_AMLZ_NEVEDENO" level={3} />
                            ) : errStatus ? (
                                <Title text="SLUZBA_NEDOSTUPNA" level={3} />
                            ) : null}
                        </LoadingWrapper>
                    </Col>
                </Row>
            ) : (
                <LoadingWrapper loading={loadingTable}>
                    <Title level={3} text="AMLZ_HISTORIE" />
                    <Row gutter={8}>
                        <Col span={24}>
                            <TableComponent
                                loading={loading}
                                columns={columns}
                                rowKey={record => record.idzadosti}
                                dataSource={zadosti?.zadosti}
                                locale={{
                                    emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                                }}
                                pagination={{
                                    total: zadosti?.count,
                                    onChange: page => setOffset((page - 1) * 10),
                                    current: offset / 10 + 1,
                                    showSizeChanger: false
                                }}
                                rowClassName={(_a, i) => {
                                    const classes = [];
                                    if (i % 2) classes.push('tableRowGray');
                                    return classes.join(' ');
                                }}
                            />
                        </Col>
                    </Row>
                </LoadingWrapper>
            )}
            <Modal
                title={<TranslateText>{'VYJADRENI_CDCP'}</TranslateText>}
                open={!!modalMessage}
                onCancel={() => setModalMessage('')}
                afterClose={() => {
                    setModalMessage('');
                }}
                footer={
                    <Row className="button-row-modal">
                        <ButtonComponent
                            id="historickeZadostiZavritBtn"
                            buttontext="ZAVRIT"
                            onClick={() => {
                                setModalMessage('');
                            }}
                        />
                    </Row>
                }
            >
                <Row>
                    <Paragraph text={modalMessage} />
                </Row>
            </Modal>
        </>
    );
};

export default PrehledEmitent;
